import { NetworkRequestObservation, RequestType } from '@quickplay/player-interface';

export interface GodavariNetworkActivity {
  /** Measured transfer speed in integer Kbps, using Sony's 1024-bit conversion. */
  event_net_download_speed: number;
  event_content_type: string;
}

export interface GodavariHeartbeatTelemetryBatch {
  bufferHealth: number[];
  networkActivity: GodavariNetworkActivity[];
}

/** Collects content measurements between heartbeats, without retaining request URLs. */
export class GodavariHeartbeatTelemetry {
  private bufferHealth: number[] = [];
  private networkActivity: GodavariNetworkActivity[] = [];
  private nextBufferPosition?: number;
  private previousPosition?: number;
  private readonly maximumSamples = 256;

  public sampleBuffer(position: number, bufferedAhead: number): void {
    if (!Number.isFinite(position) || position < 0 || !Number.isFinite(bufferedAhead) || bufferedAhead < 0) return;
    // A backward seek starts a new sample window, without fabricating samples for skipped time.
    if (this.previousPosition !== undefined && position < this.previousPosition) this.nextBufferPosition = undefined;
    this.previousPosition = position;
    if (this.nextBufferPosition !== undefined && position <= this.nextBufferPosition) return;
    this.bufferHealth.push(Math.floor(Math.min(bufferedAhead, 200)));
    if (this.bufferHealth.length > this.maximumSamples) this.bufferHealth.shift();
    this.nextBufferPosition = Math.floor(position) + 7;
  }

  public recordNetwork(observation: NetworkRequestObservation): void {
    const { requestType, bytesTransferred, durationMs, contentType } = observation;
    if (requestType !== RequestType.MANIFEST && requestType !== RequestType.SEGMENT) return;
    if (
      bytesTransferred === undefined ||
      !Number.isFinite(bytesTransferred) ||
      bytesTransferred <= 0 ||
      durationMs === undefined ||
      !Number.isFinite(durationMs) ||
      durationMs <= 0 ||
      !contentType ||
      !['mpd', 'm3u8', 'm4s', 'ts', 'mp4', 'aac', 'vtt'].includes(contentType)
    )
      return;
    const speed = Math.floor((bytesTransferred * 8 * 1000) / durationMs / 1024);
    if (!Number.isFinite(speed)) return;
    this.networkActivity.push({ event_net_download_speed: speed, event_content_type: contentType });
    if (this.networkActivity.length > this.maximumSamples) this.networkActivity.shift();
  }

  public drain(): GodavariHeartbeatTelemetryBatch {
    const batch = { bufferHealth: this.bufferHealth, networkActivity: this.networkActivity };
    this.reset();
    return batch;
  }

  public reset(): void {
    this.bufferHealth = [];
    this.networkActivity = [];
    this.nextBufferPosition = undefined;
    this.previousPosition = undefined;
  }
}
